import { ApiJsonClientError } from "@/lib/query/core/api-json-client";

/** Query value the login page reads to explain why the session ended. */
export const LOGIN_NOTICE_NO_STORE = "no-store";

/** An admin deactivated the store (403 `STORE_DEACTIVATED`, at login or
 *  mid-session). The login page signs the session out and says so. */
export const LOGIN_NOTICE_STORE_DEACTIVATED = "store-deactivated";
export const STORE_DEACTIVATED_CODE = "STORE_DEACTIVATED";

/** Codes that mean "this account no longer belongs to the store". The
 *  session path answers 401 `STORE_MEMBERSHIP_REMOVED` once the owner has
 *  removed a signed-in member (C9) — the session twin of login's 403
 *  `SELLER_NO_STORE`; an expired session stays `SELLER_SESSION_INVALID`. */
const LOST_STORE_CODES: ReadonlySet<string> = new Set([
  "SELLER_NO_STORE",
  "STORE_MEMBERSHIP_INACTIVE",
  "STORE_MEMBERSHIP_REMOVED",
]);

export function isLostStoreCode(code: string | null | undefined): boolean {
  return Boolean(code && LOST_STORE_CODES.has(code));
}

/**
 * The `?notice=` suffix for a redirect to login after a session failure: a
 * removed member is told why (the login's own "SELLER_NO_STORE" sentence),
 * anything else gets the plain login page.
 */
export function sessionLossNotice(error: unknown): string {
  if (
    !(error instanceof ApiJsonClientError) ||
    (error.status !== 401 && error.status !== 403)
  ) {
    return "";
  }
  return sessionLossNoticeForCode(error.code);
}

/** The same choice from a bare error code (the response subscriber's view). */
export function sessionLossNoticeForCode(code: string | null | undefined): string {
  if (code === STORE_DEACTIVATED_CODE) {
    return `?notice=${LOGIN_NOTICE_STORE_DEACTIVATED}`;
  }
  return isLostStoreCode(code) ? `?notice=${LOGIN_NOTICE_NO_STORE}` : "";
}
